<script setup lang="ts">
import type { Component } from "vue";
import ConditionValueBar from "./ConditionValueBar.vue";
import type { ValueRange } from "@/utils/conditionBar";
import type { TemperatureUnit } from "@/stores/settings.store";
import LoadingValue from "@/components/ui/LoadingValue.vue";

withDefaults(
  defineProps<{
    label: string;
    detail?: string;
    icon: Component;
    color: string;
    value: string;
    valueUnit?: string;
    kind: "humidity" | "wind" | "pressure";
    current: number | null;
    observed?: ValueRange | null;
    forecast?: ValueRange | null;
    scale: ValueRange;
    barUnit: string;
    unitPreference: TemperatureUnit;
    wallMode?: boolean;
    fullWidth?: boolean;
    loading?: boolean;
  }>(),
  {
    detail: undefined,
    valueUnit: undefined,
    observed: undefined,
    forecast: undefined,
    wallMode: false,
    fullWidth: false,
  },
);
</script>

<template>
  <div
    class="grid items-center"
    :class="[
      wallMode
        ? 'grid-cols-[16px_minmax(0,1fr)] gap-x-1.5 gap-y-1'
        : 'grid-cols-[22px_max-content_minmax(0,1fr)] gap-x-2.5 gap-y-2',
      { 'col-span-full': fullWidth && !wallMode },
    ]"
    :style="{ '--metric-color': color }"
  >
    <component
      :is="icon"
      class="stroke-[1.65] text-[var(--metric-color)]"
      :size="wallMode ? 16 : 22"
      aria-hidden="true"
    />
    <div
      class="leading-[1.4] text-text-muted"
      :class="wallMode ? 'text-xs tall-wall:text-[15px]' : 'text-[13px]'"
    >
      {{ label }}
      <span
        v-if="detail"
        class="mt-0.5 text-[11px]"
        :class="wallMode ? 'hidden' : 'block'"
      >
        {{ detail }}
      </span>
    </div>
    <strong
      class="leading-[1.2] font-[550] whitespace-nowrap text-[var(--metric-color)] tabular-nums [&_small]:text-xs [&_small]:leading-normal [&_small]:font-normal [&_small]:text-text-muted"
      :class="
        wallMode
          ? 'col-span-full text-left text-base tall-wall:text-xl'
          : 'text-left text-[17px]'
      "
    >
      <LoadingValue :loading="loading">
        {{ loading ? "000" : value }}
        <small v-if="valueUnit">{{ valueUnit }}</small>
      </LoadingValue>
    </strong>
    <ConditionValueBar
      v-if="!wallMode"
      class="col-[2/-1]"
      :unit-preference="unitPreference"
      :label="label"
      :kind="kind"
      :current="current"
      :observed="observed"
      :forecast="forecast"
      :scale="scale"
      :unit="barUnit"
      :loading="loading"
    />
  </div>
</template>
